import { useEffect } from 'react'

export interface ToastProps {
  message: string
  type: 'success' | 'error'
  onClose: () => void
  timeout?: number
}

/**
 * Toast notification component
 *
 * Displays a temporary notification message in the top-right corner of the screen.
 * Auto-dismisses after the specified timeout (default 4 seconds).
 *
 * @example
 * ```tsx
 * const [toast, setToast] = useState<ToastProps | null>(null)
 *
 * const showToast = (message: string, type: 'success' | 'error') => {
 *   setToast({ message, type, onClose: () => setToast(null) })
 * }
 *
 * {toast && <Toast {...toast} />}
 * ```
 */
export function Toast({ message, type, onClose, timeout = 4000 }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, timeout)
    return () => clearTimeout(timer)
  }, [onClose, timeout])

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? (
        <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ) : (
        <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      )}
      {message}
      <button onClick={onClose} className="ml-2 hover:opacity-70" aria-label="Close notification">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
